import { useLiveQuery } from "@tanstack/react-db";
import React, { createContext, useContext, useEffect, useMemo } from "react";

import { heartbeat as sendHeartbeat } from "@storyboard/web-client";
import { transport } from "@/config";
import { handsFreeEnabledForWorkspace } from "@/data/workspaceConfig";
import { useGlobalFlags } from "@/hooks/useGlobalFlags";
import UseTimeouts from "@/hooks/useTimeouts";
import { appContext, deviceContext } from "@/models/apiContext";
import { justWorkspaceMembershipsQuery, myWorkspaceMembershipsQuery, workspacesQuery } from "@/models/repositories";
import { UserContext } from "@/models/StateProviders/userProvider";
import { TelemetryContext } from "@/models/TelemetryProvider";
import type { Workspace, WorkspaceMembership } from "@/store/types";
import { cleanupStaleWorkspaceKeys } from "@/utils/localStorage";
import { MyAccountContext } from "./myAccountProvider";

type WorkspaceState = {
  currentWorkspaceId?: string;
  myCurrentWorkspaceMembership?: WorkspaceMembership;
  workspaceMemberships?: WorkspaceMembership[];
  isWorkspaceAdmin?: () => boolean;
  isWorkspaceLimitedMember?: () => boolean;
  workspaces?: Workspace[];
};

export const WorkspaceContext = createContext<WorkspaceState>({
  currentWorkspaceId: undefined,
  myCurrentWorkspaceMembership: undefined,
  workspaceMemberships: [],
  isWorkspaceAdmin: () => false,
  isWorkspaceLimitedMember: () => false,
  workspaces: [],
});
const LOCATION_ENABLED = true;
/** Shared fallback while a live query loads, so the memoized context value keeps its identity. */
const NO_ROWS: never[] = [];

const WorkspaceProvider = ({ children }) => {
  const { setAttribute } = useContext(TelemetryContext);
  const { myAccountId } = React.useContext(MyAccountContext);
  const { workspaceId } = useContext(UserContext);
  const { data: workspaceRows } = useLiveQuery(workspacesQuery);
  const workspaces = (workspaceRows ?? NO_ROWS) as Workspace[];

  const sendDesktopHandsFree = false;

  /** Same id passed to myWorkspaceMembershipsQuery — aligns with context consumers and route when UserContext lags. */
  const currentWorkspaceId: string | undefined =
    (workspaceId as string | undefined) ?? (workspaces[0]?.id as string | undefined);

  const handsFreeAllowed = handsFreeEnabledForWorkspace(currentWorkspaceId);

  useEffect(() => {
    if (currentWorkspaceId) {
      cleanupStaleWorkspaceKeys(currentWorkspaceId);
    }
  }, [currentWorkspaceId]);

  const { data: myMembershipRows } = useLiveQuery((q) =>
    myAccountId && currentWorkspaceId ? myWorkspaceMembershipsQuery(myAccountId, currentWorkspaceId)(q) : null,
  );
  const myCurrentWorkspaceMemberships: WorkspaceMembership[] = myMembershipRows ?? NO_ROWS;

  useEffect(() => {
    const urlContainsEmailParam = new URLSearchParams(window.location.search).get("email") !== null;
    if (urlContainsEmailParam) {
      const newUrl = new URL(window.location.href);
      const urlPath = newUrl?.href.replace(newUrl?.origin, "");
      window.location.assign(`storyboard://${urlPath}`);
    }
  }, []);

  const myCurrentWorkspaceMembership = myCurrentWorkspaceMemberships[0];

  const { data: workspaceMembershipRows } = useLiveQuery((q) =>
    currentWorkspaceId ? justWorkspaceMembershipsQuery(currentWorkspaceId)(q) : null,
  );
  const workspaceMemberships: WorkspaceMembership[] = workspaceMembershipRows ?? NO_ROWS;

  const isWorkspaceAdmin = React.useCallback(() => {
    return myCurrentWorkspaceMembership?.role === "admin";
  }, [myCurrentWorkspaceMembership]);

  const isWorkspaceLimitedMember = React.useCallback(() => {
    return myCurrentWorkspaceMembership?.role === "limitedMember";
  }, [myCurrentWorkspaceMembership]);

  useEffect(() => {
    if (myCurrentWorkspaceMembership?.role) {
      setAttribute("workspaceRole", myCurrentWorkspaceMembership?.role);
    }
  }, [myCurrentWorkspaceMembership?.role]);

  const localHeartBeat = UseTimeouts(60);
  useEffect(() => {
    if (!handsFreeAllowed || !sendDesktopHandsFree) return;
    if (localHeartBeat?.refresh === false) {
      if (sendDesktopHandsFree && LOCATION_ENABLED) {
        const successCallback = (position: {
          coords: {
            latitude: number;
            longitude: number;
          };
        }) => {
          const timestamp = new Date().toISOString();
          const heartbeat = {
            timestamp,
          };
          void sendHeartbeat(transport, { workspaceId: currentWorkspaceId, ...heartbeat, appContext, deviceContext });
        };
        const errorCallback = () => {
          const timestamp = new Date().toISOString();
          const heartBeatNoGeo = {
            timestamp,
          };
          void sendHeartbeat(transport, {
            workspaceId: currentWorkspaceId,
            ...heartBeatNoGeo,
            appContext,
            deviceContext,
          });
        };

        navigator.geolocation.getCurrentPosition(successCallback, errorCallback);
      } else if (sendDesktopHandsFree) {
        const timestamp = new Date().toISOString();
        const heartBeatNoGeo = {
          timestamp,
        };
        void sendHeartbeat(transport, {
          workspaceId: currentWorkspaceId,
          ...heartBeatNoGeo,
          appContext,
          deviceContext,
        });
      }
    }
  }, [handsFreeAllowed, localHeartBeat?.refresh, sendDesktopHandsFree, currentWorkspaceId]);

  const value = useMemo(
    () => ({
      currentWorkspaceId,
      myCurrentWorkspaceMembership,
      workspaceMemberships,
      workspaces,
      isWorkspaceAdmin,
      isWorkspaceLimitedMember,
    }),
    [
      currentWorkspaceId,
      myCurrentWorkspaceMembership,
      workspaceMemberships,
      workspaces,
      isWorkspaceAdmin,
      isWorkspaceLimitedMember,
    ],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

export default WorkspaceProvider;
